import { UIElement } from '../ui-element';
import { Labels } from '../../global/labels';
import { insertElements, rebuildChildren } from '../../global/ui-helpers';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import type {
    ElementConfig,
    AttributeValue,
    ObservedAttributes,
} from '../ui-element.type';
import type { TranslationLabels } from '../../types/labels.type';
import type { NullOr } from '../../global/utils.type';
import styles from './ui-upload-item.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIUploadItem
 * @element ui-upload-item
 * @classdesc Represents a class for <code>ui-upload-item</code> element.
 * @fires event:remove-upload-item
 * @property {string} filename {@attr filename} File name.
 * @property {string} filesize {@attr filesize} File size placeholder.
 * @property {string} link {@attr link} File URL that can be used to reference
 * the contents of the file.
 * @property {HTMLDivElement} wrapper Shortcut to item wrapper.
 * @property {HTMLButtonElement} removeAction Shortcut to remove button element.
 * @example
 * <ui-upload-item filename="Third file" link="#" filesize="35 Mb"></ui-upload-item>
 */
@Component({ tag: 'ui-upload-item', styles })
export class UIUploadItem extends UIElement {
    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-upload-item', {
            remove: 'Remove',
        });
    }

    /**
     * @type {string[]}
     */
    static get observedAttributes(): ObservedAttributes {
        return ['filename', 'filesize', 'link'];
    }

    @Attribute(String) filename?: string;
    @Attribute(String) filesize?: string;
    @Attribute(String) link?: string;

    @Child('.ui-upload-item__wrapper') wrapper!: HTMLDivElement;
    @Child('.ui-upload-item__button.-remove') removeAction?: HTMLButtonElement;

    private handleRemoveActionHandler: EventListener = (): void => {
        this.remove();
    };

    /**
     * Build config for uploaded file item.
     * @returns {Array<ElementConfig>}
     */
    buildFileItem(): Array<ElementConfig> {
        return [
            {
                tagName: 'a',
                attributes: {
                    class: 'ui-upload-item__link',
                    href: this.link || null,
                    target: '_blank',
                },
                children: [this.filename || ''],
            },
            this.filesize && {
                tagName: 'span',
                classList: {
                    'ui-upload-item__detail': true,
                },
                children: ['(' + this.filesize + ')'],
            },
            {
                tagName: 'ui-button',
                attributes: {
                    'class': 'ui-upload-item__button -remove -iconed',
                    'layout': 'tertiary',
                    'glyph': 'redesign-circle-x-mark',
                    'title': UIUploadItem.labels.remove,
                    'aria-label': UIUploadItem.labels.remove,
                    'data-event': 'remove-upload-item',
                },
            },
        ].filter(Boolean) as Array<ElementConfig>;
    }

    /**
     * Removes the file element.
     */
    override remove(): void {
        this.hide();
        this.parentElement?.removeChild(this);
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: AttributeValue,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        /* istanbul ignore if */
        if (!this.hydrated) {
            return;
        }
        switch (name) {
            case 'filename':
            case 'filesize':
            case 'link':
                rebuildChildren(this.wrapper, this.buildFileItem());
                break;
        }
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        insertElements(this, [
            {
                tagName: 'div',
                classList: {
                    'ui-upload-item__wrapper': true,
                },
                children: this.buildFileItem(),
            },
        ]);
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        if (this.removeAction) {
            this.removeAction.addEventListener(
                'click',
                this.handleRemoveActionHandler
            );
        }
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        if (this.removeAction) {
            this.removeAction.removeEventListener(
                'click',
                this.handleRemoveActionHandler
            );
        }
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        if (this.removeAction) {
            this.removeAction.addEventListener(
                'click',
                this.handleRemoveActionHandler
            );
        }
    }
}
